import { createContext, useContext, useState, useRef, useEffect, useMemo } from 'react';
import { useEditorPreferences } from '../features/settings/hooks/useEditorPreferences';
import { resolveDarkMode } from '../utils/theme';

const UIStateContext = createContext(null);

export const useUIState = () => {
  const context = useContext(UIStateContext);
  if (!context) {
    throw new Error('useUIState must be used within UIStateProvider');
  }
  return context;
};

export const UIStateProvider = ({ children }) => {
  // Theme -- derived from the shared `isDarkMode` preference (follows the user
  // across devices; undefined = follow the OS). There is no separate copy here.
  const { preferences, updatePreference, preferencesRef } = useEditorPreferences();
  const isDarkMode = resolveDarkMode(preferences.isDarkMode);

  // Sidebar states
  const [showProjectTree, setShowProjectTree] = useState(true);
  const [showRightSidebar, setShowRightSidebar] = useState(true);
  const [isLeftSidebarCollapsed, setIsLeftSidebarCollapsed] = useState(false);
  const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState(false);
  // MainSidebar (the app-chrome Chat/Editor file-tree column, one level up
  // in AppShell) used to toggle its own open/collapsed width as local state
  // -- moved here so other routed pages can read the real current width too
  // (e.g. the editor positions EditorMinimap off of it).
  const [isMainSidebarCollapsed, setIsMainSidebarCollapsed] = useState(false);
  const [leftSidebarWidth, setLeftSidebarWidth] = useState(280);
  const [rightSidebarWidth, setRightSidebarWidth] = useState(420);
  const [isResizingLeft, setIsResizingLeft] = useState(false);
  const [isResizingRight, setIsResizingRight] = useState(false);

  // Panel states
  const [activeActivityItem, setActiveActivityItem] = useState('files');
  const [activeRightPanel, setActiveRightPanel] = useState('chat');
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [showOutlinePanel, setShowOutlinePanel] = useState(false);
  const [showAnnotationsPanel, setShowAnnotationsPanel] = useState(false);

  // Modal states
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showShortcutHelper, setShowShortcutHelper] = useState(false);
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [showAnnotationDropdown, setShowAnnotationDropdown] = useState(false);
  const [showPageSizeDropdown, setShowPageSizeDropdown] = useState(false);
  const [showWelcomeFlow, setShowWelcomeFlow] = useState(null);

  // Editor view states
  const [isPageView, setIsPageView] = useState(true);
  const [isFooterVisible, setIsFooterVisible] = useState(true);
  const [pageSize, setPageSize] = useState('us-letter');
  const [zoomLevel, setZoomLevel] = useState(100);
  const [isAutoFit, setIsAutoFit] = useState(false);

  // Toolbar/dropdown positions
  const [commandPosition, setCommandPosition] = useState({ top: 0, left: 0 });
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });

  // Autocomplete states
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompletePosition, setAutocompletePosition] = useState({ x: 0, y: 0 });
  const [autocompleteSelectedIndex, setAutocompleteSelectedIndex] = useState(0);

  // Navigation indicators
  const [showAnnotationNavIndicators, setShowAnnotationNavIndicators] = useState(false);
  const [showPinnedLocationNavIndicators, setShowPinnedLocationNavIndicators] = useState(false);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);

  // Import progress
  const [activeImportJobId, setActiveImportJobId] = useState(null);

  // Theme transition
  const [isThemeTransitioning, setIsThemeTransitioning] = useState(false);

  // Refs
  const pageSizeDropdownRef = useRef(null);

  const actions = useMemo(() => ({
    // Theme
    toggleDarkMode: () => {
      setIsThemeTransitioning(true);
      // Change theme IMMEDIATELY so it's ready underneath the overlay
      updatePreference('isDarkMode', !resolveDarkMode(preferencesRef.current.isDarkMode));

      // End transition after animation completes
      setTimeout(() => {
        setIsThemeTransitioning(false);
      }, 800);
    },

    // Sidebar toggles
    toggleProjectTree: () => {
      setShowProjectTree(prev => !prev);
    },

    toggleRightSidebar: () => {
      setShowRightSidebar(prev => !prev);
    },

    toggleLeftSidebarCollapse: () => {
      setIsLeftSidebarCollapsed(prev => !prev);
    },

    toggleMainSidebarCollapse: () => {
      setIsMainSidebarCollapsed(prev => !prev);
    },

    setMainSidebarCollapsed: (collapsed) => {
      setIsMainSidebarCollapsed(collapsed);
    },

    setLeftSidebarCollapsed: (collapsed) => {
      setIsLeftSidebarCollapsed(collapsed);
    },

    toggleRightSidebarCollapse: () => {
      setIsRightSidebarCollapsed(prev => !prev);
    },

    setRightSidebarVisible: (visible) => {
      setShowRightSidebar(visible);
    },

    setRightSidebarCollapsed: (collapsed) => {
      setIsRightSidebarCollapsed(collapsed);
    },

    // Sidebar sizing
    setLeftSidebarWidth: (width) => {
      setLeftSidebarWidth(width);
    },

    setRightSidebarWidth: (width) => {
      setRightSidebarWidth(width);
    },

    setIsResizingLeft: (resizing) => {
      setIsResizingLeft(resizing);
    },

    setIsResizingRight: (resizing) => {
      setIsResizingRight(resizing);
    },

    // Panel management
    setActiveActivityItem: (item) => {
      setActiveActivityItem(item);
    },

    setActiveRightPanel: (panel) => {
      setActiveRightPanel(panel);
    },

    toggleNotesPanel: () => {
      setShowNotesPanel(prev => !prev);
    },

    toggleOutlinePanel: () => {
      setShowOutlinePanel(prev => !prev);
    },

    toggleAnnotationsPanel: () => {
      setShowAnnotationsPanel(prev => !prev);
    },

    // Modals
    openTemplateSelector: () => {
      setShowTemplateSelector(true);
    },

    closeTemplateSelector: () => {
      setShowTemplateSelector(false);
    },

    openShortcutHelper: (position) => {
      setCommandPosition(position);
      setShowShortcutHelper(true);
    },

    closeShortcutHelper: () => {
      setShowShortcutHelper(false);
    },

    openNoteEditor: () => {
      setShowNoteEditor(true);
    },

    closeNoteEditor: () => {
      setShowNoteEditor(false);
    },

    openAnnotationDropdown: (position) => {
      setDropdownPosition(position);
      setShowAnnotationDropdown(true);
    },

    closeAnnotationDropdown: () => {
      setShowAnnotationDropdown(false);
    },

    togglePageSizeDropdown: () => {
      setShowPageSizeDropdown(prev => !prev);
    },

    setShowWelcomeFlow: (show) => {
      setShowWelcomeFlow(show);
    },

    // View settings
    togglePageView: () => {
      setIsPageView(prev => !prev);
    },

    toggleFooterVisible: () => {
      setIsFooterVisible(prev => !prev);
    },

    setPageSize: (size) => {
      setPageSize(size);
    },

    setZoomLevel: (level) => {
      setZoomLevel(level);
    },

    zoomIn: () => {
      setIsAutoFit(false);
      setZoomLevel(prev => Math.min(200, prev + 10));
    },

    zoomOut: () => {
      setIsAutoFit(false);
      setZoomLevel(prev => Math.max(50, prev - 10));
    },

    toggleAutoFit: () => {
      setIsAutoFit(prev => {
        if (!prev) {
          setZoomLevel(100);
        }
        return !prev;
      });
    },

    // Autocomplete
    showAutocompleteDropdown: (position, selectedIndex = 0) => {
      setAutocompletePosition(position);
      setAutocompleteSelectedIndex(selectedIndex);
      setShowAutocomplete(true);
    },

    hideAutocomplete: () => {
      setShowAutocomplete(false);
      setAutocompleteSelectedIndex(0);
    },

    setAutocompleteSelectedIndex: (index) => {
      setAutocompleteSelectedIndex(index);
    },

    // Navigation indicators
    toggleAnnotationNavIndicators: () => {
      setShowAnnotationNavIndicators(prev => {
        const newValue = !prev;
        if (newValue) {
          setShowAnnotationsPanel(true);
          setShowPinnedLocationNavIndicators(false);
        }
        return newValue;
      });
    },

    togglePinnedLocationNavIndicators: () => {
      setShowPinnedLocationNavIndicators(prev => {
        if (!prev) {
          setShowAnnotationNavIndicators(false);
        }
        return !prev;
      });
    },

    // Recording
    startRecording: () => {
      setIsRecording(true);
    },

    stopRecording: () => {
      setIsRecording(false);
    },

    // Import progress
    setActiveImportJobId: (jobId) => {
      setActiveImportJobId(jobId);
    },

    // Refs
    getPageSizeDropdownRef: () => pageSizeDropdownRef
  }), [updatePreference, preferencesRef]);

  const value = useMemo(() => ({
    state: {
      isDarkMode,
      showProjectTree,
      showRightSidebar,
      isLeftSidebarCollapsed,
      isRightSidebarCollapsed,
      isMainSidebarCollapsed,
      leftSidebarWidth,
      rightSidebarWidth,
      isResizingLeft,
      isResizingRight,
      activeActivityItem,
      activeRightPanel,
      showNotesPanel,
      showOutlinePanel,
      showAnnotationsPanel,
      showTemplateSelector,
      showShortcutHelper,
      showNoteEditor,
      showAnnotationDropdown,
      showPageSizeDropdown,
      showWelcomeFlow,
      isPageView,
      isFooterVisible,
      pageSize,
      zoomLevel,
      isAutoFit,
      commandPosition,
      dropdownPosition,
      showAutocomplete,
      autocompletePosition,
      autocompleteSelectedIndex,
      showAnnotationNavIndicators,
      showPinnedLocationNavIndicators,
      isRecording,
      activeImportJobId,
      isThemeTransitioning,
      pageSizeDropdownRef
    },
    actions
  }), [
    isDarkMode, showProjectTree, showRightSidebar, isLeftSidebarCollapsed,
    isRightSidebarCollapsed, isMainSidebarCollapsed, leftSidebarWidth, rightSidebarWidth,
    isResizingLeft, isResizingRight, activeActivityItem, activeRightPanel,
    showNotesPanel, showOutlinePanel, showAnnotationsPanel, showTemplateSelector,
    showShortcutHelper, showNoteEditor, showAnnotationDropdown, showPageSizeDropdown,
    showWelcomeFlow, isPageView, isFooterVisible, pageSize, zoomLevel, isAutoFit,
    commandPosition, dropdownPosition, showAutocomplete, autocompletePosition,
    autocompleteSelectedIndex, showAnnotationNavIndicators, showPinnedLocationNavIndicators,
    isRecording, activeImportJobId, isThemeTransitioning, actions
  ]);

  return (
    <UIStateContext.Provider value={value}>
      {children}
    </UIStateContext.Provider>
  );
};

